import {
  Entity,
  LinesheetState,
  Row,
  SortDirection,
  SortKey,
  SortScope,
} from './reducers/linesheet';

export enum RootActionType {
  BlurLinesheetInDOM = 'BlurLinesheetInDOM',
  ClearLinesheetRows = 'ClearLinesheetRows',
  FillLinesheetWithEmptyCasepacks = 'FillLinesheetWithEmptyCasepacks',
  FillLinesheetWithEmptyItems = 'FillLinesheetWithEmptyItems',
  FocusLinesheetInDOM = 'FocusLinesheetInDOM',
  MoveLinesheetFocusedCellBackward = 'MoveLinesheetFocusedCellBackward',
  MoveLinesheetFocusedCellDown = 'MoveLinesheetFocusedCellDown',
  MoveLinesheetFocusedCellForward = 'MoveLinesheetFocusedCellForward',
  MoveLinesheetFocusedCellLeft = 'MoveLinesheetFocusedCellLeft',
  MoveLinesheetFocusedCellRight = 'MoveLinesheetFocusedCellRight',
  MoveLinesheetFocusedCellUp = 'MoveLinesheetFocusedCellUp',
  ReceiveLinesheetAttributes = 'ReceiveLinesheetAttributes',
  ReceiveLinesheetColumnCount = 'ReceiveLinesheetColumnCount',
  ReceiveLinesheetFilterKey = 'ReceiveLinesheetFilterKey',
  ReceiveLinesheetFilterRollups = 'ReceiveLinesheetFilterRollups',
  ReceiveLinesheetRows = 'ReceiveLinesheetRows',
  ReceiveLinesheetTotalCasepackCasesInView = 'ReceiveLinesheetTotalCasepackCasesInView',
  ReceiveLinesheetTotalUnitsInView = 'ReceiveLinesheetTotalUnitsInView',
  ReceiveLinesheetAvailableQuantity = 'ReceiveLinesheetAvailableQuantity',
  ReceiveMoreLinesheetRows = 'ReceiveMoreLinesheetRows',
  RemoveLinesheetSort = 'RemoveLinesheetSort',
  ResetLinesheetFocus = 'ResetLinesheetFocus',
  ResetLinesheetSelections = 'ResetLinesheetSelections',
  ResetLinesheetSort = 'ResetLinesheetSort',
  SetIsLinesheetStale = 'SetIsLinesheetStale',
  SetIsSimulatingLoading = 'SetIsSimulatingLoading',
  SetLinesheetEntity = 'SetLinesheetEntity',
  SetLinesheetFocusedCell = 'SetLinesheetFocusedCell',
  SetLinesheetHiddenColumns = 'SetLinesheetHiddenColumns',
  SetLinesheetSort = 'SetLinesheetSort',
  SetLinesheetViewableIndexes = 'SetLinesheetViewableIndexes',
  SetLinesheetSavedMarketUuid = 'SetLinesheetSavedMarketUuid',
  SetLinesheetSavedUploadChannelUuid = 'SetLinesheetSavedUploadChannelUuid',
  SetLinesheetScrollToIndex = 'SetLinesheetScrollToIndex',
  ToggleMobileFiltersModal = 'ToggleMobileFiltersModal',
  ToggleSelectAllLinesheetRows = 'ToggleSelectAllLinesheetRows',
  ToggleSelectLinesheetRow = 'ToggleSelectLinesheetRow',
  ToggleUploadInventoryModal = 'ToggleUploadInventoryModal',
}

export type RootAction =
  | BlurLinesheetInDOM
  | ClearLinesheetRows
  | FillLinesheetWithEmptyCasepacks
  | FillLinesheetWithEmptyItems
  | FocusLinesheetInDOM
  | MoveLinesheetFocusedCellBackward
  | MoveLinesheetFocusedCellDown
  | MoveLinesheetFocusedCellForward
  | MoveLinesheetFocusedCellLeft
  | MoveLinesheetFocusedCellRight
  | MoveLinesheetFocusedCellUp
  | ReceiveLinesheetAttributes
  | ReceiveLinesheetColumnCount
  | ReceiveLinesheetFilterKey
  | ReceiveLinesheetFilterRollups
  | ReceiveLinesheetRows
  | ReceiveLinesheetTotalCasepackCasesInView
  | ReceiveLinesheetTotalUnitsInView
  | ReceiveLinesheetAvailableQuantity
  | ReceiveMoreLinesheetRows
  | RemoveLinesheetSort
  | ResetLinesheetFocus
  | ResetLinesheetSort
  | ResetLinesheetSelections
  | SetIsLinesheetStale
  | SetIsSimulatingLoading
  | SetLinesheetEntity
  | SetLinesheetFocusedCell
  | SetLinesheetHiddenColumns
  | SetLinesheetSavedMarketUuid
  | SetLinesheetSavedUploadChannelUuid
  | SetLinesheetScrollToIndex
  | SetLinesheetSort
  | SetLinesheetViewableIndexes
  | ToggleMobileFiltersModal
  | ToggleSelectAllLinesheetRows
  | ToggleSelectLinesheetRow
  | ToggleUploadInventoryModal;

type BlurLinesheetInDOM = {
  type: RootActionType.BlurLinesheetInDOM;
};

type ClearLinesheetRows = {
  type: RootActionType.ClearLinesheetRows;
};

type FillLinesheetWithEmptyCasepacks = {
  type: RootActionType.FillLinesheetWithEmptyCasepacks;
};

type FillLinesheetWithEmptyItems = {
  type: RootActionType.FillLinesheetWithEmptyItems;
};

type FocusLinesheetInDOM = {
  type: RootActionType.FocusLinesheetInDOM;
};

type MoveLinesheetFocusedCellBackward = {
  type: RootActionType.MoveLinesheetFocusedCellBackward;
};

type MoveLinesheetFocusedCellDown = {
  type: RootActionType.MoveLinesheetFocusedCellDown;
};

type MoveLinesheetFocusedCellForward = {
  type: RootActionType.MoveLinesheetFocusedCellForward;
};

type MoveLinesheetFocusedCellLeft = {
  type: RootActionType.MoveLinesheetFocusedCellLeft;
};

type MoveLinesheetFocusedCellRight = {
  type: RootActionType.MoveLinesheetFocusedCellRight;
};

type MoveLinesheetFocusedCellUp = {
  type: RootActionType.MoveLinesheetFocusedCellUp;
};

type ReceiveLinesheetAttributes = {
  type: RootActionType.ReceiveLinesheetAttributes;
  payload: readonly { label: string; inturnType: string }[];
};

type ReceiveLinesheetColumnCount = {
  type: RootActionType.ReceiveLinesheetColumnCount;
  payload: number;
};

type ReceiveLinesheetFilterKey = {
  type: RootActionType.ReceiveLinesheetFilterKey;
  payload: string;
};

type ReceiveLinesheetFilterRollups = {
  type: RootActionType.ReceiveLinesheetFilterRollups;
  payload: LinesheetState['filterRollups'];
};

type ReceiveLinesheetRows = {
  type: RootActionType.ReceiveLinesheetRows;
  payload: Row[];
};

type ReceiveLinesheetTotalCasepackCasesInView = {
  type: RootActionType.ReceiveLinesheetTotalCasepackCasesInView;
  payload: number | null;
};

type ReceiveLinesheetTotalUnitsInView = {
  type: RootActionType.ReceiveLinesheetTotalUnitsInView;
  payload: number;
};

type ReceiveLinesheetAvailableQuantity = {
  type: RootActionType.ReceiveLinesheetAvailableQuantity;
  payload: number;
};

type ReceiveMoreLinesheetRows = {
  type: RootActionType.ReceiveMoreLinesheetRows;
  payload: {
    offset: number;
    rows: Row[];
  };
};

type RemoveLinesheetSort = {
  type: RootActionType.RemoveLinesheetSort;
};

type ResetLinesheetFocus = {
  type: RootActionType.ResetLinesheetFocus;
};

type ResetLinesheetSelections = {
  type: RootActionType.ResetLinesheetSelections;
};

type ResetLinesheetSort = {
  type: RootActionType.ResetLinesheetSort;
};

type SetIsLinesheetStale = {
  type: RootActionType.SetIsLinesheetStale;
  payload: boolean;
};

type SetIsSimulatingLoading = {
  type: RootActionType.SetIsSimulatingLoading;
  payload: boolean;
};

type SetLinesheetEntity = {
  type: RootActionType.SetLinesheetEntity;
  payload: Entity;
};

type SetLinesheetFocusedCell = {
  type: RootActionType.SetLinesheetFocusedCell;
  payload: {
    column: number;
    row: number;
  };
};

type SetLinesheetHiddenColumns = {
  type: RootActionType.SetLinesheetHiddenColumns;
  payload: Set<string>;
};

type SetLinesheetSavedMarketUuid = {
  type: RootActionType.SetLinesheetSavedMarketUuid;
  payload: string;
};

type SetLinesheetSavedUploadChannelUuid = {
  type: RootActionType.SetLinesheetSavedUploadChannelUuid;
  payload: string;
};

type SetLinesheetScrollToIndex = {
  type: RootActionType.SetLinesheetScrollToIndex;
  payload: number;
};

type SetLinesheetSort = {
  type: RootActionType.SetLinesheetSort;
  payload: {
    direction: SortDirection | null;
    key: SortKey;
    scope: SortScope | null;
    scopeValue: string | null;
  };
};

type SetLinesheetViewableIndexes = {
  type: RootActionType.SetLinesheetViewableIndexes;
  payload: {
    startIndex: number;
    stopIndex: number;
  };
};

type ToggleMobileFiltersModal = {
  type: RootActionType.ToggleMobileFiltersModal;
};

type ToggleSelectAllLinesheetRows = {
  type: RootActionType.ToggleSelectAllLinesheetRows;
};

type ToggleSelectLinesheetRow = {
  type: RootActionType.ToggleSelectLinesheetRow;
  payload: string;
};

type ToggleUploadInventoryModal = {
  type: RootActionType.ToggleUploadInventoryModal;
  payload: boolean;
};
